// What an organiser can register as genuinely theirs.
export const CHANNEL_TYPES = Object.freeze({
  email_domain: 'Email domain',      // anything @techsummit.events
  email: 'Email address',            // one address on someone else's domain
  url_domain: 'Website',             // techsummit.events and its subdomains
  phone: 'Phone number',
  bank: 'Bank account',              // stored as a keyed hash + last four digits only
  social: 'Social account',          // a profile URL
  hotel: 'Official hotel booking',   // a booking URL
  contractor: 'Official contractor', // a contractor's website or email domain
});

// What a verify / report lookup can be about.
export const INDICATOR_TYPES = Object.freeze(['email', 'domain', 'url', 'phone', 'bank']);

// The answers the verify page and the forwarding address can give. Never "safe":
// only whether something matches what the organiser registered.
export const VERDICTS = Object.freeze({
  official: 'official',   // matches one of the event's official channels
  not_ours: 'not_ours',   // not one of the event's channels
  reported: 'reported',   // not ours AND reported as a scam (here or at other events)
  invalid: 'invalid',     // couldn't tell what the input is
});

// Event slugs live at summitshield.io/<slug>, beside the marketing pages, so these
// can never be an event slug. Add every new top-level marketing path here.
export const RESERVED_SLUGS = Object.freeze(new Set([
  'about', 'admin', 'api', 'app', 'assets', 'b', 'blog', 'contact', 'docs', 'faq',
  'features', 'help', 'legal', 'login', 'pricing', 'privacy', 'security', 'signup',
  'status', 'support', 'terms', 'v', 'validate', 'verify', 'www', '_astro', 'favicon.ico', 'robots.txt',
  'sitemap.xml', 'solutions', 'how-it-works', 'guides', 'for-exhibitors', 'demo',
]));

// Redis. Every SummitShield key starts with "ss", so it can share a Redis server
// with other apps (locally: Just Attend) without colliding. apps/mail repeats these
// two strings because Haraka plugins are CommonJS.
export const QUEUE_PREFIX = 'ss';             // BullMQ keys: ss:<queue>:...
export const RCPT_SET = 'ss:rcpt';            // exact addresses the mail servers accept (honeypot)
export const RCPT_DOMAINS_SET = 'ss:rcpt_domains'; // domains where ANY address is accepted (live events)
// Live events of companies in TEST MODE: hash of event mail domain -> JSON array of
// the sender domains allowed (the company's own claimed email domains). Mail from
// anyone else is refused at SMTP. (docs/company-identity-plan.md 5.1)
export const RCPT_TEST_HASH = 'ss:rcpt_test';

/** The mail domain an event receives on: <slug>.check.summitshield.io */
export function eventMailDomain(slug, checkDomain = 'check.summitshield.io') {
  return `${String(slug).toLowerCase()}.${String(checkDomain).toLowerCase()}`;
}

export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;   // 3–50 characters

/**
 * Turn whatever was typed for an event's web address into a slug: people often paste
 * their own website ("https://www.techsummit.events/tickets"). Drops the scheme, www.
 * and any path/query, then lower-cases and turns dots/spaces into hyphens:
 * → "techsummit-events". The portal has the same function (pages/events/EventModal.jsx).
 */
export function slugFromInput(input) {
  return String(input || '').trim().toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')   // http://, https://
    .replace(/^www\d*\./, '')                  // www., www2.
    .replace(/[/?#].*$/, '')                     // path, query, fragment
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')                 // dots, spaces, anything else
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)
    .replace(/-+$/, '');
}

// ---- Company identity (docs/company-identity-plan.md) ------------------------

// What can own identifiers. A company has brands; events belong to a brand (or the
// company directly); people and suppliers are linked by relationships.
export const ENTITY_KINDS = Object.freeze(['organisation', 'brand', 'event', 'person', 'supplier']);

// What a company, brand or event can register as genuinely its own.
export const IDENTIFIER_TYPES = Object.freeze({
  email_domain: 'Email domain',      // anything @techsummit.events
  web_domain: 'Website',             // techsummit.events and its subdomains
  email_address: 'Email address',    // one address, usually on someone else's domain
  phone: 'Phone number',
  bank: 'Bank account',              // stored as a keyed hash + last four digits only
  social: 'Social account',          // a profile URL
  url: 'Web page or booking link',   // one page, e.g. the official hotel booking link
});

// Who outside the company can see an identifier.
//   public        listed on the verify page, and offered as the official alternative
//   confirm_only  never listed; a check of this exact value answers "verified"
//   private       internal matching only; never part of an outside answer
export const VISIBILITIES = Object.freeze({
  public: 'Shown on the verify page',
  confirm_only: 'Confirmed when asked, never listed',
  private: 'Private',
});

// Identifier type -> the channel type the verdict engine matches on (verdict.js).
export const IDENTIFIER_TO_CHANNEL = Object.freeze({
  email_domain: 'email_domain',
  web_domain: 'url_domain',
  email_address: 'email',
  phone: 'phone',
  bank: 'bank',
  social: 'social',
  url: 'hotel',          // the engine's "exact page" matcher
});

// Free email services: an address there can be someone's, but the domain is never
// a company's own. Refused as an email_domain / web_domain identifier.
export const FREE_MAIL_DOMAINS = Object.freeze(new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'hotmail.co.uk', 'live.com', 'live.co.uk',
  'msn.com', 'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
  'proton.me', 'protonmail.com', 'pm.me', 'gmx.com', 'gmx.net', 'gmx.de', 'web.de', 'mail.com', 'zoho.com',
  'yandex.com', 'yandex.ru', 'mail.ru', 'qq.com', '163.com', '126.com', 'btinternet.com', 'sky.com',
  'virginmedia.com', 'talktalk.net', 'fastmail.com', 'hey.com', 'tutanota.com', 'tuta.io',
]));
